import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebaseConfig';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Navigation from './components/Navigation';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Marketplace from './pages/Marketplace';
import DatasetDetail from './pages/DatasetDetail';
import MyLibrary from './pages/MyLibrary';
import DatasetExport from './pages/DatasetExport';
import ExportsHistory from './pages/ExportsHistory';
import Billing from './pages/Billing';
import './App.css';

type ProtectedRouteProps = {
  loading: boolean;
  user: User | null;
  children: React.ReactNode;
};

function ProtectedRoute({ loading, user, children }: ProtectedRouteProps) {
  if (loading) {
    return <div className="app-loading">Loading Console...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppLayout({ user, children }: { user: User; children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  
  // Pass sidebarOpen to Marketplace component if it's a Marketplace
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Marketplace) {
      return React.cloneElement(child, { sidebarOpen } as any);
    }
    return child;
  });
  
  return (
    <div className="app-shell">
      <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen((prev) => !prev)} />
      <div className="app-shell__content" style={{ marginLeft: sidebarOpen ? 220 : 72 }}>
        <Navigation user={user} />
        <main className="app-shell__main">{enhancedChildren}</main>
      </div>
    </div>
  );
}

function AppContent() {
  const { colors } = useTheme();
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);
  
  return (
    <div className="app-root" style={{ background: colors.background, color: colors.text }}>
      <BrowserRouter>
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/dashboard"
          element={
              <ProtectedRoute loading={authLoading} user={user}>
                {user && (
                  <AppLayout user={user}>
                    <Dashboard user={user} />
                  </AppLayout>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace"
            element={
              <ProtectedRoute loading={authLoading} user={user}>
                {user && (
                  <AppLayout user={user}>
                    <Marketplace user={user} />
              </AppLayout>
                )}
            </ProtectedRoute>
          }
        />
        <Route
            path="/marketplace/:datasetId"
          element={
              <ProtectedRoute loading={authLoading} user={user}>
                {user && (
                  <AppLayout user={user}>
                    <DatasetDetail user={user} />
              </AppLayout>
                )}
            </ProtectedRoute>
          }
        />
        <Route
            path="/library"
          element={
              <ProtectedRoute loading={authLoading} user={user}>
                {user && (
                  <AppLayout user={user}>
                    <MyLibrary user={user} />
              </AppLayout>
                )}
            </ProtectedRoute>
          }
        />
        <Route
            path="/library/:datasetId/export"
          element={
              <ProtectedRoute loading={authLoading} user={user}>
                {user && (
                  <AppLayout user={user}>
                    <DatasetExport user={user} />
              </AppLayout>
                )}
            </ProtectedRoute>
          }
        />
        <Route
            path="/exports-history"
          element={
              <ProtectedRoute loading={authLoading} user={user}>
                {user && (
                  <AppLayout user={user}>
                    <ExportsHistory user={user} />
              </AppLayout>
                )}
            </ProtectedRoute>
          }
        />
        <Route
            path="/billing"
          element={
              <ProtectedRoute loading={authLoading} user={user}>
                {user && (
                  <AppLayout user={user}>
                    <Billing user={user} />
              </AppLayout>
                )}
            </ProtectedRoute>
          }
        />
          <Route
            path="/"
            element={<Navigate to={user ? '/dashboard' : '/login'} replace />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
